import dayjs from 'dayjs';
import { Link2Off, Receipt } from 'lucide-react';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import useGetExpense from '@/features/dashboard/expenses/hooks/useGetExpense.ts';
import useDeleteTransactionAllocation from '@/features/dashboard/financialTransactions/hooks/useDeleteTransactionAllocation.ts';
import ExpenseFormSkeleton from '@/features/dashboard/expenses/components/ExpenseFormSkeleton.tsx';
import {
  getExpensePaymentStatusLabel,
  getExpensePaymentStatusTone,
} from '@/features/dashboard/expenses/types/ExpensePaymentStatus.utils.ts';
import { remainingToPay } from '@/features/dashboard/expenses/types/TourExpenseDto.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';

interface ExpenseDetailsModalProps {
  tourId: string;
  expenseId: string;
  onClose: () => void;
}

const ExpenseDetailsModal = ({
  tourId,
  expenseId,
  onClose,
}: ExpenseDetailsModalProps) => {
  const { data: expense, isLoading } = useGetExpense(tourId, expenseId);
  const unpinMutation = useDeleteTransactionAllocation();
  const { confirmDeleteWithCallback } = confirmDialogUseHook();

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Szczegóły wydatku" onClose={onClose} />
      <ModalContent>
        {isLoading || !expense ? (
          <ExpenseFormSkeleton />
        ) : (
          <div className="space-y-4">
            <div className="rounded-md bg-neutral-50 p-3">
              <InfoCardList className="flex-1 space-y-1">
                <InfoCardRow layout="columns" label="Status">
                  <StatusDot
                    tone={getExpensePaymentStatusTone(expense.status)}
                    label={getExpensePaymentStatusLabel(expense.status)}
                  />
                </InfoCardRow>
                <InfoCardRow layout="columns" label="Kwota faktury">
                  {formatPrice(expense.amount.amount, expense.amount.currency)}
                </InfoCardRow>
                <InfoCardRow layout="columns" label="Zapłacono">
                  {formatPrice(expense.paidAmount, expense.amount.currency)}
                </InfoCardRow>
                <InfoCardRow layout="columns" label="Termin płatności">
                  {dayjs(expense.dueDate).format('DD.MM.YYYY')}
                </InfoCardRow>
                {expense.description && (
                  <InfoCardRow layout="columns" label="Opis">
                    {expense.description}
                  </InfoCardRow>
                )}
              </InfoCardList>
            </div>

            <div>
              <h4 className="mb-2 text-xs font-medium uppercase text-neutral-500">
                Wpięte zapłaty
              </h4>

              {expense.allocations.length === 0 ? (
                <NoItems
                  icon={<Receipt />}
                  title="Brak zapłat"
                  description="Zarejestruj zapłatę albo dopasuj wydatek do przelewu z wyciągu."
                />
              ) : (
                <ul className="space-y-2">
                  {expense.allocations.map((allocation) => (
                    <li
                      key={allocation.id}
                      className="flex items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-neutral-800">
                          {formatPrice(
                            Math.abs(allocation.amount.amount),
                            allocation.amount.currency,
                          )}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {dayjs(allocation.transactionDate).format(
                            'DD.MM.YYYY',
                          )}
                          {allocation.notes ? ` · ${allocation.notes}` : ''}
                        </div>
                      </div>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <ActionButton
                            tone="dangerGhost"
                            size="xs"
                            iconOnly
                            aria-label="Odepnij zapłatę"
                            isLoading={unpinMutation.isPending}
                            onClick={() =>
                              confirmDeleteWithCallback({
                                title: 'Odepnij zapłatę',
                                description: (
                                  <>
                                    Zapłata{' '}
                                    <b>
                                      {formatPrice(
                                        Math.abs(allocation.amount.amount),
                                        allocation.amount.currency,
                                      )}
                                    </b>{' '}
                                    zostanie odpięta od wydatku. Sama transakcja
                                    zostaje i wróci do puli do rozdysponowania.
                                  </>
                                ),
                                confirmText: 'Odepnij',
                                cancelText: 'Wróć',
                                onConfirm: () =>
                                  unpinMutation.mutate({
                                    transactionId: allocation.transactionId,
                                    allocationId: allocation.id,
                                  }),
                              })
                            }
                          >
                            <Link2Off />
                          </ActionButton>
                        </TooltipTrigger>
                        <TooltipContent>Odepnij zapłatę</TooltipContent>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Także przy zerze zapłat — brak wiersza czytało się jak brak długu. */}
            <div className="flex justify-between border-t border-neutral-200 pt-3 text-sm">
              <span className="text-neutral-600">Pozostało do pokrycia</span>
              <span className="font-medium text-neutral-900">
                {formatPrice(remainingToPay(expense), expense.amount.currency)}
              </span>
            </div>
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
          Zamknij
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default ExpenseDetailsModal;
